import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Eye, RefreshCw, Clock, Send, Mail, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TIME_SLOTS = [
  "9:00 AM - 11:00 AM",
  "11:00 AM - 1:00 PM",
  "1:00 PM - 3:00 PM",
  "3:00 PM - 5:00 PM"
];

export default function PendingScheduleInvitation() {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [familyToSchedule, setFamilyToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(null);

  useEffect(() => {
    fetchPendingFamilies();
  }, []);

  const fetchPendingFamilies = async () => {
    setLoading(true);
    try {
      // Get families who have completed profile and received welcome email but haven't received scheduling invite yet
      const response = await api.get('/families/staged');
      // Filter to only show those with welcome_email_sent but NOT scheduling_invited
      const pendingInvite = response.data.filter(f => 
        f.welcome_email_sent && !f.scheduling_invited
      );
      setFamilies(pendingInvite);
    } catch (error) {
      toast.error('Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = (family) => {
    setSelectedFamily(family);
    setShowDetailModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const openScheduleModal = (family) => {
    setFamilyToSchedule(family);
    setScheduleDate('');
    setScheduleTime('');
    setShowScheduleModal(true);
  };

  const handleManualSchedule = async () => {
    if (!familyToSchedule || !scheduleDate || !scheduleTime) {
      toast.error('Please select both a date and time slot');
      return;
    }
    setScheduling(true);
    try {
      const response = await api.post('/admin/manual-schedule-delivery', {
        family_id: familyToSchedule.id,
        delivery_date: scheduleDate,
        delivery_time: scheduleTime
      });
      toast.success(response.data.message);
      setFamilies(prev => prev.filter(f => f.id !== familyToSchedule.id));
      setShowScheduleModal(false);
      setFamilyToSchedule(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule delivery');
    } finally {
      setScheduling(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const sendInvitation = async (family) => {
    setSendingId(family.id);
    try {
      await api.post('/families/send-scheduling-invitations', [family.id]);
      toast.success(`Schedule invitation sent to ${family.parent_first_name} ${family.parent_last_name}`);
      setFamilies(prev => prev.filter(f => f.id !== family.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingId(null);
    }
  };

  const resendWelcomeEmail = async (family) => {
    setResendingWelcome(family.id);
    try {
      const response = await api.post(`/admin/resend-welcome-email/${family.id}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend welcome email');
    } finally {
      setResendingWelcome(null);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 p-8 ml-64">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground" data-testid="page-title">Pending Schedule Invitation</h1>
              <p className="text-stone-600 mt-1">Families awaiting their scheduling invitation (within 24-48 hours of registration)</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={fetchPendingFamilies} disabled={loading} className="rounded-full">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="border-blue-200 bg-blue-50 mb-6">
            <CardContent className="p-4">
              <p className="text-blue-800 text-sm">
                <strong>Workflow:</strong> These families have completed their profiles and received their automatic welcome letter. 
                Send them a scheduling invitation so they can pick their delivery date. After sending, they'll move to "New Families".
              </p>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-stone-600">Loading families...</p>
            </div>
          ) : families.length === 0 ? (
            <Card className="border-stone-200 shadow-card">
              <CardContent className="py-12 text-center">
                <Clock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-600">No Families Pending</h3>
                <p className="text-stone-500 mt-1">Families will appear here after completing their registration and receiving their welcome letter.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {families.map((family) => (
                <Card key={family.id} className="border-stone-200 shadow-card hover:shadow-floating transition-all" data-testid={`pending-family-${family.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-foreground">
                            {family.parent_first_name} {family.parent_last_name}
                          </h3>
                          <Badge className="bg-amber-100 text-amber-800">
                            <Clock className="w-3 h-3 mr-1" />
                            Awaiting Schedule Invite
                          </Badge>
                        </div>
                        <div className="text-sm text-stone-600 space-y-1">
                          <p>{family.email} • {family.phone}</p>
                          <p>{family.location_type}: {family.location_detail}</p>
                          <p>{family.children?.length || 0} child{(family.children?.length || 0) !== 1 ? 'ren' : ''}</p>
                          <p className="text-xs text-stone-400">Registered: {formatDate(family.created_at)}</p>
                          {family.welcome_email_sent_at && (
                            <p className="text-xs text-green-600">Welcome email sent: {formatDate(family.welcome_email_sent_at)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => sendInvitation(family)}
                          disabled={sendingId === family.id}
                          data-testid={`send-invite-${family.id}`}
                          className="rounded-full"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {sendingId === family.id ? 'Sending...' : 'Send Schedule Invitation'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => resendWelcomeEmail(family)}
                          disabled={resendingWelcome === family.id}
                          data-testid={`resend-welcome-${family.id}`}
                          className="rounded-full"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          {resendingWelcome === family.id ? 'Sending...' : 'Resend Welcome Email'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openScheduleModal(family)}
                          data-testid={`schedule-delivery-${family.id}`}
                          className="rounded-full"
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Schedule Delivery
                        </Button>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewDetails(family)}
                            data-testid={`view-${family.id}`}
                            className="rounded-full"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Link to={`/families/${family.id}`}>
                            <Button variant="outline" size="sm" className="rounded-full">
                              Details
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Family Details</DialogTitle>
            <DialogDescription>
              {selectedFamily?.parent_first_name} {selectedFamily?.parent_last_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedFamily && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-500">Parent Name</label>
                  <p className="text-foreground">{selectedFamily.parent_first_name} {selectedFamily.parent_last_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Role</label>
                  <p className="text-foreground">{selectedFamily.parent_role || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Email</label>
                  <p className="text-foreground">{selectedFamily.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Phone</label>
                  <p className="text-foreground">{selectedFamily.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Location</label>
                  <p className="text-foreground">{selectedFamily.location_type}: {selectedFamily.location_detail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Address</label>
                  <p className="text-foreground">{selectedFamily.address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Joined</label>
                  <p className="text-foreground">{formatDate(selectedFamily.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Delivery Status</label>
                  <p className="text-foreground">
                    {selectedFamily.delivery_confirmed ? (
                      <span className="text-green-600 font-medium">✅ Confirmed</span>
                    ) : selectedFamily.has_scheduled_delivery ? (
                      <span className="text-blue-600">⏳ Pending Confirmation</span>
                    ) : (
                      <span className="text-amber-600">📅 Not Scheduled</span>
                    )}
                  </p>
                </div>
              </div>
              
              {selectedFamily.confirmed_delivery_date && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-medium text-green-800">Scheduled Delivery</p>
                  <p className="text-green-700">{formatDate(selectedFamily.confirmed_delivery_date)} at {selectedFamily.confirmed_delivery_time}</p>
                </div>
              )}
              
              {selectedFamily.children && selectedFamily.children.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-stone-500 block mb-2">Children ({selectedFamily.children.length})</label>
                  <div className="space-y-2">
                    {selectedFamily.children.map((child, idx) => (
                      <div key={idx} className="bg-stone-50 p-3 rounded-lg">
                        <p className="font-medium">{child.name}, Age {child.age}</p>
                        <p className="text-sm text-stone-600">
                          {child.gender} • {child.learning_stage} • Reading: {child.reading_level}
                        </p>
                        {child.interests && <p className="text-sm text-stone-500">Interests: {child.interests}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4 border-t">
                <Link to={`/families/${selectedFamily.id}`} className="flex-1">
                  <Button className="w-full rounded-full">
                    <Eye className="w-4 h-4 mr-2" />
                    Full Details
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Delivery Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-delivery-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Delivery</DialogTitle>
            <DialogDescription>
              Manually schedule a delivery for {familyToSchedule?.parent_first_name} {familyToSchedule?.parent_last_name}. 
              A confirmation email will be sent to the family.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {familyToSchedule && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-900">{familyToSchedule.parent_first_name} {familyToSchedule.parent_last_name}</p>
                <p className="text-sm text-blue-700">{familyToSchedule.email} • {familyToSchedule.phone}</p>
                <p className="text-sm text-blue-700">{familyToSchedule.address}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="pending-schedule-date">Delivery Date</Label>
              <Input
                id="pending-schedule-date"
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={getMinDate()}
                data-testid="schedule-date-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pending-schedule-time">Delivery Time Slot</Label>
              <Select value={scheduleTime} onValueChange={setScheduleTime}>
                <SelectTrigger data-testid="schedule-time-select" className="h-11">
                  <SelectValue placeholder="Select a time slot" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowScheduleModal(false); setFamilyToSchedule(null); }}
              className="rounded-full"
              data-testid="cancel-schedule-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualSchedule}
              disabled={scheduling || !scheduleDate || !scheduleTime}
              className="rounded-full"
              data-testid="confirm-schedule-button"
            >
              {scheduling ? 'Scheduling...' : 'Confirm & Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
